import type { GetScoreChallengeResult } from "./ScoreChallengeClient";
import type { ScoreChallengeView } from "./scoreChallenge";

/**
 * Pure decision logic for turning a `ScoreChallengeClient.getChallenge()`
 * result into what `ScoreChallengeScene` should show - no Phaser
 * dependency, so it's directly unit-testable (see
 * `src/daily/__tests__/scoreChallengeLookup.test.ts`) without a Scene, the
 * same "extract the decision, not just the network call" pattern
 * `attemptStatus.ts`/`attemptReservation.ts` already use for Daily
 * Challenge.
 *
 * `"unavailable"` is the ONE outcome every kind of lookup-infrastructure
 * failure converges to - the RPC not existing yet (migration `006` not
 * applied), a network rejection, a malformed/unexpected response shape,
 * anything not cleanly resolved into a real active/expired/invalid/self
 * result. It is deliberately indistinguishable from `"invalid"` in the UI
 * (`ScoreChallengeScene` renders both as `CHALLENGE UNAVAILABLE` / `But
 * today's Daily is ready.` / `PLAY TODAY'S DAILY`) - a visitor can't tell,
 * and shouldn't need to, whether a link is genuinely fake or just
 * temporarily unverifiable; both must fail into the exact same safe,
 * friendly, working state, never a blank screen or a dead end.
 */
export type ScoreChallengeLookupOutcome =
  | { type: "unavailable" }
  | { type: "invalid" }
  | { type: "expired" }
  | { type: "self" }
  | { type: "active"; view: ScoreChallengeView & { challengeId: string; targetDailyPoints: number } };

export function resolveScoreChallengeLookup(result: GetScoreChallengeResult): ScoreChallengeLookupOutcome {
  // Covers "RPC/function unavailable" (migration 006 not applied yet -
  // PostgREST 404s the call), "network rejection" (offline/timeout), and
  // any other non-"ok" service state - `ScoreChallengeClient.getChallenge()`
  // itself never throws (see its own doc), it always resolves to one of
  // these states instead.
  if (result.state !== "ok" || !result.view) return { type: "unavailable" };

  const view = result.view;
  if (view.status === "invalid") return { type: "invalid" };
  if (view.status === "expired") return { type: "expired" };

  // "malformed lookup response": a status claiming "active" but missing a
  // field an active result must always have, OR a status value outside
  // the three known ones entirely (a future/unexpected server change) -
  // never guessed at or defaulted, always treated as unavailable rather
  // than risking a crash or nonsensical UI further down the line.
  if (view.status !== "active" || view.challengeId === null || view.targetDailyPoints === null) {
    return { type: "unavailable" };
  }

  if (view.isCreator) return { type: "self" };
  return { type: "active", view: { ...view, challengeId: view.challengeId, targetDailyPoints: view.targetDailyPoints } };
}
